import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExportButtons = ({ type, roomId = null, days = 7, className = '' }) => {
  const [exporting, setExporting] = useState(false);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      let url = '';
      let filename = '';
      
      switch (type) {
        case 'calendar':
          url = `${API}/export/calendar?format=${format}`;
          filename = `farm_calendar_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
          break;
        case 'production':
          url = `${API}/export/production-report?format=${format}`;
          filename = `production_report_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
          break;
        case 'room-analytics':
          if (!roomId) {
            toast.error('Room not selected');
            return;
          }
          url = `${API}/export/room-analytics/${roomId}?format=${format}&days=${days}`;
          filename = `room_analytics_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
          break;
        case 'batch-history':
          url = `${API}/export/batch-history?format=${format}`;
          filename = `batch_history_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
          break;
        default:
          toast.error('Unknown export type');
          return;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Export downloaded successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          disabled={exporting}
          className={className}
          data-testid={`export-${type}-btn`}
        >
          <Download className="w-4 h-4 mr-2" />
          {exporting ? 'Exporting...' : 'Export'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem 
          onClick={() => handleExport('excel')}
          data-testid={`export-${type}-excel`}
        >
          <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
          Export as Excel
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleExport('pdf')}
          data-testid={`export-${type}-pdf`}
        >
          <FileText className="w-4 h-4 mr-2 text-red-600" />
          Export as PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportButtons;
